import type * as React from 'react';
import type { OptionProps } from 'react-apollo';
import { graphql } from 'react-apollo';

import { branch, compose, withHandlers, withProps } from 'recompose';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import user from 'js/lib/user';

import degreeEnrollmentChecklistsV1Mutation from 'bundles/premium-certificate/components/mastertrack/banner/DegreeEnrollmentChecklistsV1Mutation';
import type {
  degreeEnrollmentChecklistsV1Query as DegreeEnrollmentChecklistsV1QueryData,
  degreeEnrollmentChecklistsV1QueryVariables,
} from 'bundles/premium-certificate/components/mastertrack/banner/__generated__/degreeEnrollmentChecklistsV1Query';
import { degreeEnrollmentChecklistsV1Query } from 'bundles/premium-certificate/components/mastertrack/banner/queries';
import { PROGRAM_TYPES } from 'bundles/premium-certificate/constants/constants';
import type {
  ChecklistApiProps,
  ChecklistStatus,
  EnrollmentChecklistDataProps,
  EnrollmentChecklistItem,
  EnrollmentChecklistStatuses,
} from 'bundles/premium-certificate/constants/types';
import { getChecklistStatus } from 'bundles/premium-certificate/utils/checklistUtils';

type HocInputProps = {
  slug: string;
  enrollmentChecklistItems?: Array<EnrollmentChecklistItem>;
};

type ChecklistDataFromGql = {
  enrollmentChecklistItemStatuses?: EnrollmentChecklistStatuses;
  enrollmentChecklistStatus: ChecklistStatus;
  isLoadingChecklistData: boolean;
};

type HandlerProps = { onEnrollButtonClick: (event: React.SyntheticEvent<HTMLButtonElement>) => void };

type PropsWithRouter = HocInputProps & { router: InjectedRouter; productId?: string };
type PropsWithMastertrackId = PropsWithRouter & { masterTrackId?: string };
type PropsWithProductId = HocInputProps & { masterTrackId: string; productId?: string }; // Types 3+ are used only when we know masterTrackId exists
type PropsWithGraphqlData = PropsWithProductId & ChecklistDataFromGql;

type mutationProps = {
  variables: {
    id: string;
    body?: Object;
  };
};

export type PropsWithMutation = PropsWithGraphqlData & {
  callChecklistApi: (props: mutationProps) => Promise<void>;
};

const createUserMastertrackId = (slug: string, userId: string | number): string =>
  tupleToStringKey([userId, PROGRAM_TYPES.MASTERTRACK, slug]);

export const queryResultToProps = (
  props: OptionProps<
    PropsWithProductId,
    DegreeEnrollmentChecklistsV1QueryData,
    degreeEnrollmentChecklistsV1QueryVariables
  >
): ChecklistDataFromGql => {
  const { data, ownProps } = props;
  const enrollmentChecklistItemStatuses = (((data || {}).DegreeEnrollmentChecklistsV1Resource || {}).get || {})
    .completedItems;
  const enrollmentChecklistStatus = getChecklistStatus({ ...ownProps, enrollmentChecklistItemStatuses });
  const isLoadingChecklistData = (data?.loading && user.get().authenticated) || false;
  return {
    enrollmentChecklistItemStatuses,
    enrollmentChecklistStatus,
    isLoadingChecklistData,
  };
};

type ApiPropsToGqlOptionsFunc = (props: ChecklistApiProps) => { variables: { id: string } };

export const propsToApiParams: ApiPropsToGqlOptionsFunc = (props) => ({
  variables: { id: props.masterTrackId },
});

const withEnrollmentChecklistData = graphql<
  PropsWithProductId,
  DegreeEnrollmentChecklistsV1QueryData,
  degreeEnrollmentChecklistsV1QueryVariables,
  ChecklistDataFromGql
>(degreeEnrollmentChecklistsV1Query, {
  options: (props) => ({
    ...propsToApiParams(props),
    fetchPolicy: 'cache-and-network',
  }),
  props: queryResultToProps,
});

const withMutations = graphql<
  PropsWithGraphqlData,
  DegreeEnrollmentChecklistsV1QueryData,
  degreeEnrollmentChecklistsV1QueryVariables,
  PropsWithMutation
>(degreeEnrollmentChecklistsV1Mutation, {
  name: 'callChecklistApi',
  options: (props: ChecklistApiProps) => {
    const { masterTrackId } = props;
    return {
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          // Auto refetch query after all mutations
          query: degreeEnrollmentChecklistsV1Query,
          variables: { id: masterTrackId },
          props: queryResultToProps,
        },
      ],
      fetchPolicy: 'no-cache',
      ssr: false,
    };
  },
});

const withMasterTrackId = withProps(({ slug }: { slug: string }) => {
  const userId = user.get().id;
  return slug && userId ? { masterTrackId: createUserMastertrackId(slug, userId) } : undefined;
});

export const subscribeUserToChecklist = async ({ callChecklistApi, masterTrackId }: PropsWithMutation) => {
  await callChecklistApi({ variables: { id: masterTrackId, body: {} } });
};

export default <T extends EnrollmentChecklistDataProps>(component: React.ComponentType<T>) =>
  compose<T, Omit<T, keyof EnrollmentChecklistDataProps> & HocInputProps>(
    connectToRouter<PropsWithRouter, Omit<PropsWithRouter, 'router'>>((router) => ({ router })),
    withMasterTrackId,
    branch<PropsWithMastertrackId>(
      ({ masterTrackId }) => !!masterTrackId, // The following only runs when there is a logged-in user, as userId is part of masterTrackId
      compose(
        withEnrollmentChecklistData,
        withMutations,
        // Set onEnrollButtonClick when there is a logged-in user
        withHandlers<PropsWithMutation, HandlerProps>({
          onEnrollButtonClick: (props: PropsWithMutation) => () => subscribeUserToChecklist(props),
        })
      )
    )
  )(component);
